import React, { useState, useRef } from 'react';
import { toPng, toJpeg } from 'html-to-image';
import { toast } from 'react-toastify';
import {
    DevicePhoneMobileIcon,
    ComputerDesktopIcon,
    DeviceTabletIcon,
    ArrowDownTrayIcon,
    DocumentDuplicateIcon
} from '@heroicons/react/24/outline';

/**
 * Email Preview Component
 * 
 * Displays a live preview of email content with:
 * - Multiple device size presets (Desktop, Mobile, Tablet)
 * - Email client-specific rendering modes
 * - Sample email context (From/To/Subject)
 * - Screenshot export functionality
 * - HTML copy to clipboard
 */

const EmailPreview = ({ htmlContent, designSettings = {}, mode = 'footer' }) => {
    const [deviceMode, setDeviceMode] = useState('desktop'); // desktop, mobile, tablet
    const [emailClient, setEmailClient] = useState('gmail'); // gmail, outlook, apple
    const previewRef = useRef(null);

    const deviceSizes = {
        desktop: { width: '650px', label: 'Desktop', icon: ComputerDesktopIcon },
        tablet: { width: '480px', label: 'Tablet', icon: DeviceTabletIcon },
        mobile: { width: '320px', label: 'Mobile', icon: DevicePhoneMobileIcon }
    };

    const emailClients = {
        gmail: {
            name: 'Gmail',
            backgroundColor: '#f2f6fc',
            containerBg: '#ffffff'
        },
        outlook: {
            name: 'Outlook',
            backgroundColor: '#ffffff',
            containerBg: '#ffffff'
        },
        apple: {
            name: 'Apple Mail',
            backgroundColor: '#e5e5e5',
            containerBg: '#ffffff'
        }
    };

    const exportAsImage = async (format = 'png') => {
        if (!previewRef.current) return;

        try {
            const dataUrl = format === 'png'
                ? await toPng(previewRef.current, { quality: 0.95 })
                : await toJpeg(previewRef.current, { quality: 0.95 });

            const link = document.createElement('a');
            link.download = `email-${mode}-${Date.now()}.${format}`;
            link.href = dataUrl;
            link.click();

            toast.success(`Exported as ${format.toUpperCase()}`);
        } catch (error) {
            console.error('Export error:', error);
            toast.error('Failed to export image');
        }
    };

    const copyToClipboard = () => {
        if (!htmlContent) return;

        // Create a properly formatted email HTML with inline styles
        const emailHTML = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
</head>
<body style="margin: 0; padding: 0; font-family: ${designSettings.fontFamily || 'Arial, sans-serif'}; background-color: #f4f4f4;">
  <table role="presentation" cellpadding="0" cellspacing="0" width="100%" style="margin: 0; padding: 0;">
    <tr>
      <td style="padding: 20px 0;">
        <table role="presentation" cellpadding="0" cellspacing="0" width="${designSettings.containerWidth || '600px'}" style="margin: 0 auto; background-color: ${designSettings.backgroundColor || '#ffffff'}; border-radius: 8px;">
          <tr>
            <td style="padding: 20px;">
              ${htmlContent}
            </td>
          </tr>
        </table>
      </td>
    </tr>
  </table>
</body>
</html>`;

        navigator.clipboard.writeText(emailHTML).then(() => {
            toast.success('HTML copied to clipboard');
        }).catch((error) => {
            console.error('Copy error:', error);
            toast.error('Failed to copy to clipboard');
        });
    };

    const getSampleEmailContent = () => {
        return (
            <div style={{ fontFamily: designSettings.fontFamily || 'Arial, sans-serif', fontSize: '14px', lineHeight: '1.6', color: '#333' }}>
                <p style={{ margin: '0 0 16px 0' }}>Dear Valued Customer,</p>
                <p style={{ margin: '0 0 16px 0' }}>
                    Thank you for choosing our services. We're committed to providing you with the best experience possible.
                </p>
                <p style={{ margin: '0 0 16px 0' }}>
                    If you have any questions or need assistance, please don't hesitate to reach out to our support team.
                </p>
                <p style={{ margin: '0 0 16px 0' }}>
                    Best regards,<br />
                    The Support Team
                </p>
            </div>
        );
    };

    return (
        <div className="email-preview-container flex flex-col h-full">
            {/* Toolbar */}
            <div className="bg-white border-b border-gray-200 p-3">
                <div className="flex items-center justify-between mb-3">
                    <h3 className="text-sm font-medium text-gray-900">Live Preview</h3>

                    <div className="flex items-center space-x-2">
                        {/* Export Buttons */}
                        <button
                            onClick={copyToClipboard}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            title="Copy HTML"
                        >
                            <DocumentDuplicateIcon className="h-4 w-4" />
                        </button>
                        <button
                            onClick={() => exportAsImage('png')}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded transition-colors"
                            title="Export as PNG"
                        >
                            <ArrowDownTrayIcon className="h-4 w-4" />
                        </button>
                    </div>
                </div>

                {/* Device Mode Selector */}
                <div className="flex items-center space-x-2 mb-2">
                    <span className="text-xs font-medium text-gray-700">Device:</span>
                    {Object.entries(deviceSizes).map(([key, { label, icon: Icon }]) => (
                        <button
                            key={key}
                            onClick={() => setDeviceMode(key)}
                            className={`flex items-center px-3 py-1.5 text-xs rounded transition-colors ${deviceMode === key
                                ? 'bg-blue-100 text-blue-700 font-medium'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                        >
                            <Icon className="h-4 w-4 mr-1" />
                            {label}
                        </button>
                    ))}
                </div>

                {/* Email Client Selector */}
                <div className="flex items-center space-x-2">
                    <span className="text-xs font-medium text-gray-700">Client:</span>
                    {Object.entries(emailClients).map(([key, { name }]) => (
                        <button
                            key={key}
                            onClick={() => setEmailClient(key)}
                            className={`px-3 py-1.5 text-xs rounded transition-colors ${emailClient === key
                                ? 'bg-blue-100 text-blue-700 font-medium'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                        >
                            {name}
                        </button>
                    ))}
                </div>
            </div>

            {/* Email Preview */}
            <div
                className="flex-1 overflow-auto p-6"
                style={{
                    backgroundColor: emailClients[emailClient].backgroundColor
                }}
            >
                <div className="mx-auto transition-all duration-300" style={{ width: deviceSizes[deviceMode].width }}>
                    {/* Email Header (mockup) */}
                    <div className="bg-white rounded-t-lg shadow-sm border border-gray-200 p-4 mb-0">
                        <div className="text-xs space-y-2">
                            <div className="flex items-start">
                                <span className="font-medium text-gray-500 w-16">From:</span>
                                <span className="text-gray-900">connect@twilightfinland.eu</span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-medium text-gray-500 w-16">To:</span>
                                <span className="text-gray-900">customer@example.com</span>
                            </div>
                            <div className="flex items-start">
                                <span className="font-medium text-gray-500 w-16">Subject:</span>
                                <span className="text-gray-900">Important Update from Twilight Finland Oy</span>
                            </div>
                        </div>
                    </div>

                    {/* Email Body */}
                    <div
                        ref={previewRef}
                        className="bg-white rounded-b-lg shadow-lg border border-t-0 border-gray-200 overflow-hidden"
                        style={{
                            backgroundColor: emailClients[emailClient].containerBg
                        }}
                    >
                        <div className="p-0">
                            {/* Sample Email Content */}
                            <div className="p-6 pb-0">
                                {getSampleEmailContent()}
                            </div>

                            {/* Footer/Signature Separator */}
                            {mode === 'signature' && (
                                <div className="my-6 border-t border-gray-300"></div>
                            )}

                            {/* User's Footer/Signature */}
                            <div
                                className="email-content-preview"
                                style={{
                                    fontFamily: designSettings.fontFamily || 'Arial, sans-serif',
                                    fontSize: '14px',
                                    lineHeight: 'normal'
                                }}
                                dangerouslySetInnerHTML={{ __html: htmlContent || '<p style="color: #999; font-style: italic;">Your content will appear here...</p>' }}
                            />
                        </div>
                    </div>

                    {/* Device Info Badge */}
                    <div className="mt-4 text-center">
                        <span className="inline-block px-3 py-1 bg-gray-800 text-white text-xs rounded-full">
                            {deviceSizes[deviceMode].label} • {emailClients[emailClient].name}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EmailPreview;
